/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import LazyImage, { LazyImageProps } from "@gcg/MobiusOverrides/LazyImage/LazyImage";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, ShipmentPackageLineDto } from "@insite/client-framework/Types/ApiModels";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    orderLines: OrderLineModel[];
    orderLineShipments: ShipmentPackageLineDto[];
}

export interface OrderDetailsShipmentDetailsProps {
    tableHeaderStyle?: DataTableHeaderProps;
    tableRowStyle?: DataTableRowProps;
    tableCellStyle?: DataTableCellProps;
    orderLineCardImage?: LazyImageProps;
    shippingDetailsTableHeaderStyle?: DataTableHeadProps;
    tableCellGridStyle?: GridItemProps;
    hiddenHeaderStyle?: HiddenProps;
}

export const OrderDetailsShipmentDetailsInfoStyles: OrderDetailsShipmentDetailsProps = {};

type Props = OwnProps;

const styles = OrderDetailsShipmentDetailsInfoStyles;

const OrderDetailsShipmentInfoDetails: FC<Props> = ({ orderLines, orderLineShipments }) => {
    const headerColumns = [
        `${translate("Product")}`,
        `${translate("Product #")}`,
        `${translate("Qty Ordered")}`,
        `${translate("Qty Shipped")}`,
    ];

    return (
        <DataTable
            css={css`
                width: 100%;
            `}
        >
            <DataTableHead {...styles.shippingDetailsTableHeaderStyle}>
                {headerColumns.map((column, index) => (
                    <DataTableHeader
                        key={uniqueId()}
                        {...styles.tableHeaderStyle}
                    >
                        {column}
                    </DataTableHeader>
                ))}
            </DataTableHead>
            <DataTableBody>
                {orderLineShipments.map((orderLineShipment: any) => {
                    return (
                        <DataTableRow key={orderLineShipment.id} {...styles.tableRowStyle}>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[0]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    <LazyImage
                                        {...styles.orderLineCardImage}
                                        src={orderLines[orderLineShipment.orderLineId].mediumImagePath}
                                    />
                                </GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[1]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    {orderLines[orderLineShipment.orderLineId]?.productErpNumber || "– –"}
                                </GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[2]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>{orderLineShipment.qtyOrdered}</GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[3]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>{orderLineShipment.qtyShipped}</GridItem>
                            </DataTableCell>
                        </DataTableRow>
                    );
                })}
            </DataTableBody>
        </DataTable>
    );
};

export default OrderDetailsShipmentInfoDetails;
